"use client";

import { Button } from "@/components/atoms/Button";
import { Card, CardContent } from "@/components/atoms/Card";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/atoms/Drawer";
import { Heading } from "@/components/atoms/Heading";
import { Paragraph } from "@/components/atoms/Paragraph";
import { UcConnectApp } from "@/components/atoms/UcConnectApp";
import { RequiredPermissionsList } from "@/components/molecules/RequiredPermissionsList";
import ConnectedAppAndProjectsPic from "@/components/organisms/InstallApplicationDrawer/connectedAppAndProjectsPic";
import InstallAppDrawerSkeleton from "@/components/organisms/InstallApplicationDrawer/InstallAppDrawerSkeleton";
import type { IInstallApplicationDrawerProps } from "@/components/organisms/InstallApplicationDrawer/types";
import { useApplicationByAliasQuery } from "@/hooks/apis/queries/useApplicationByAliasQuery";
import { useApplicationPermissionsQuery } from "@/hooks/apis/queries/useApplicationPermissionsQuery";
import { useProjectByIdQuery } from "@/hooks/apis/queries/useProjectByIdQuery";
import { useAppRouter } from "@/hooks/common/useAppRouter";
import { useTranslations } from "@/hooks/common/useTranslations";

const InstallApplicationDrawer = ({
  open,
  onClose,
  appAlias,
  projectId,
}: IInstallApplicationDrawerProps) => {
  const { t } = useTranslations("installAppDrawer");
  const { appPush } = useAppRouter();
  const { applicationByAlias, isLoadingApplicationByAlias } =
    useApplicationByAliasQuery({
      appAlias,
      enabled: true,
    });

  const { projectById, isLoadingProjectById } = useProjectByIdQuery(
    false,
    projectId,
  );

  const { name, logoUrl, oauthClientId } = applicationByAlias;

  const { applicationPermissions, isLoadingAppPermissions } =
    useApplicationPermissionsQuery(oauthClientId);

  const isLoadingInstallAppDrawer =
    isLoadingApplicationByAlias ||
    isLoadingProjectById ||
    isLoadingAppPermissions;

  const installAppHandler = () => {
    if (projectById?.id) {
      appPush(`/${appAlias}/projects/${projectById.id}/checkout`);
    }
  };

  return (
    <Drawer
      open={open}
      onDrag={(_, drag) => {
        if (drag > 0.5) {
          onClose();
        }
      }}
    >
      <DrawerContent className="h-[95%]">
        {isLoadingInstallAppDrawer ? (
          <InstallAppDrawerSkeleton />
        ) : (
          <>
            <DrawerHeader className="flex justify-between items-center">
              <Heading size="h3" fontWeight="bold" className="max-sm:text-2xl">
                {name}
              </Heading>
              <div className="flex gap-2">
                <DrawerTitle />
                <Button variant="outline" onClick={onClose}>
                  <DrawerTitle />
                  <Paragraph size="small">{t("close")}</Paragraph>
                </Button>

                <Button onClick={installAppHandler}>
                  <Paragraph size="small">
                    {t(oauthClientId ? "authorize" : "install")}
                  </Paragraph>
                </Button>
              </div>
            </DrawerHeader>
            <div className="w-full max-w-[1280px] mx-auto mt-12 px-4">
              <Card className="w-full p-6 max-sm:px-0">
                <CardContent>
                  <div className="flex flex-row gap-5 items-center justify-center text-center">
                    <ConnectedAppAndProjectsPic name={name} logoUrl={logoUrl} />
                    <UcConnectApp />
                    <ConnectedAppAndProjectsPic
                      name={projectById?.name}
                      logoUrl={projectById?.logoUrl}
                    />
                  </div>
                </CardContent>
              </Card>
              <RequiredPermissionsList items={applicationPermissions.scopes} />

              <div className="mt-3">
                <Button variant="link" className="text-primary-selected pl-0">
                  {/* <a href="asdasd" target="_blank" rel="noreferrer"> */}
                  <Paragraph size="small" fontWeight="medium">
                    {t("devPrivacyPolicy")}
                  </Paragraph>
                  {/* </a> */}
                </Button>
                <Button variant="link" className="text-primary-selected pl-0">
                  {/* <a href="asdasd" target="_blank" rel="noreferrer"> */}
                  <Paragraph size="small" fontWeight="medium">
                    {t("dataPolicy")}
                  </Paragraph>
                  {/* </a> */}
                </Button>
              </div>
            </div>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default InstallApplicationDrawer;
